class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    const cached = this.cachedMap.get(url);
    const drawerHydrated = FoxTheme.FacetsLazy.targets.drawer.hydrated;

    if (cached && cached.drawerHydrated === drawerHydrated) {
      this.renderSectionFromCache(url, event);
    } else {
      this.renderSectionFromFetch(url, event);
    }

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    const lazy = FoxTheme.FacetsLazy;

    Promise.all([
      fetch(url).then((response) => response.text()),
      lazy.targets.drawer.hydrated ? lazy.fetchSectionHtmlSafe('drawer', url) : Promise.resolve(''),
    ]).then(([sectionHtml, drawerHtml]) => {
      const responseText = sectionHtml + lazy.wrap('drawer', drawerHtml);
      const execution = performance.now() - start;

      setTimeout(
        () => {
          try {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderFacetFiltersContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, { responseText: responseText, drawerHydrated: lazy.targets.drawer.hydrated });
          } catch (error) {
            console.warn('[facets] renderSectionFromFetch render failed', error);
          } finally {
            this.afterRenderSection();
          }
        },
        execution > 250 ? 0 : 250
      );
    }).catch((error) => {
      console.warn('[facets] renderSectionFromFetch failed', error);
      this.afterRenderSection();
    });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const cached = this.cachedMap.get(url);
      const responseText = cached.responseText;
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const containers = ['FacetFiltersContainer', 'MobileFacetFiltersContainer', 'DrawerFacetFiltersContainer'].filter(
      (id) => parsedHTML.getElementById(id)
    );

    if (containers.length === 0) return;

    const selector = containers.map((id) => `#${id} [data-filter]`).join(', ');
    const facetElements = parsedHTML.querySelectorAll(selector);
    const facetElementsFormDom = document.querySelectorAll(selector);

    if (facetElements.length === 0) return;

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);

FoxTheme.FacetsLazy = {
  targets: {
    drawer: {
      holder: '[data-facet-lazy]',
      section: 'facets-drawer-content',
      wrapper: 'DrawerFacetsHydrated',
      container: 'DrawerFacetFiltersContainer',
      hydrated: false,
      loading: false,
    },
    // Not a holder-replacement target like `drawer` — `fetchSectionHtml` only needs `section`.
    // Consumed by expandFilter(), which grafts one filter group out of the response.
    full: {
      section: 'facets-full-content',
    },
  },
  buildUrl(sectionName, fromUrl) {
    const url = new URL(fromUrl || window.location.href, window.location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionName);
    return url.toString();
  },
  async fetchSectionHtml(key, fromUrl) {
    const target = this.targets[key];
    const response = await fetch(this.buildUrl(target.section, fromUrl));
    if (!response.ok) throw new Error(`facets ${target.section}: ${response.status}`);
    const data = await response.json();
    return data[target.section] || '';
  },
  async fetchSectionHtmlSafe(key, fromUrl) {
    try {
      return await this.fetchSectionHtml(key, fromUrl);
    } catch (error) {
      console.warn('[facets] section fetch failed', key, error);
      return '';
    }
  },
  wrap(key, html) {
    return html ? `<div id="${this.targets[key].container}">${html}</div>` : '';
  },
  gridSectionId() {
    const grid = document.getElementById('ProductGridContainer');
    const wrapper = grid ? grid.closest('[id^="shopify-section-"]') : null;
    if (wrapper) return wrapper.id.replace('shopify-section-', '');
    const form = document.querySelector('form[is="facet-form"][data-section-id]');
    return form ? form.getAttribute('data-section-id') : null;
  },
  syncSectionId(root) {
    const sectionId = this.gridSectionId();
    if (!sectionId) return;
    root.querySelectorAll('[data-section-id]').forEach((element) => {
      element.setAttribute('data-section-id', sectionId);
    });
  },
  applyExpandState(root, list) {
    if (!list) return;
    const groups = list.split(',').map((item) => item.trim());
    root.querySelectorAll('details[data-filter]').forEach((details) => {
      const labelNode = details.querySelector('summary span');
      const raw = labelNode && labelNode.childNodes[0] ? labelNode.childNodes[0].textContent : '';
      const label = raw.trim().toLowerCase();
      if (label && groups.includes(label)) details.open = true;
    });
  },
  showRetryUI(key) {
    const target = this.targets[key];
    const live = document.querySelector(target.holder);
    if (!live || !live.isConnected) return;

    const strings = FoxTheme.facetsStrings || {};
    const message = document.createElement('p');
    message.className = 'text-sm text-subtext';
    message.setAttribute('role', 'alert');
    message.textContent = strings.loadError || 'Filters failed to load.';
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'btn btn--secondary';
    retryButton.textContent = strings.retry || 'Retry';
    retryButton.addEventListener(
      'click',
      () => {
        retryButton.disabled = true;
        this.hydrate(key);
      },
      { once: true }
    );
    live.replaceChildren(message, retryButton);
  },
  applyHiddenGroups(root, list) {
    if (!list) return;
    const groups = list.split(',').map((item) => item.trim());
    root.querySelectorAll('details[data-filter]').forEach((details) => {
      const labelNode = details.querySelector('summary span');
      const raw = labelNode && labelNode.childNodes[0] ? labelNode.childNodes[0].textContent : '';
      const label = raw.trim().toLowerCase();
      if (label && groups.includes(label)) details.remove();
    });
  },
  async hydrate(key) {
    const target = this.targets[key];
    if (!target || target.hydrated || target.loading) return;
    const holder = document.querySelector(target.holder);
    if (!holder) return;
    target.expandGroups = (holder.dataset.expandGroups || '').toLowerCase();
    target.hiddenGroups = (holder.dataset.hiddenGroups || '').toLowerCase();
    target.loading = true;
    try {
      const html = await this.fetchSectionHtml(key);
      const live = holder.isConnected ? holder : document.querySelector(target.holder);
      if (!live) return;
      const content = html
        ? new DOMParser().parseFromString(html, 'text/html').getElementById(target.wrapper)
        : null;
      if (!content) {
        target.retried = false;
        this.showRetryUI(key);
        return;
      }
      this.syncSectionId(content);
      this.applyHiddenGroups(content, target.hiddenGroups);
      this.applyExpandState(content, target.expandGroups);
      live.replaceWith(content);
      target.hydrated = true;
    } catch (error) {
      console.warn('[facets] hydrate failed', key, error);
      if (!target.retried) {
        target.retried = true;
        target.loading = false;
        setTimeout(() => this.hydrate(key), 1500);
        return;
      }
      target.retried = false;
      // Retry already failed once too: stop retrying silently and let the shopper trigger it.
      this.showRetryUI(key);
    } finally {
      target.loading = false;
    }
  },
  fullCacheKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return url.toString();
  },
  fullFacets() {
    // One request serves every filter group, so it is fetched at most once per filter state.
    // The key is the current URL because the response carries counts and checked state for it.
    const key = this.fullCacheKey();
    if (!this.fullRequest || this.fullRequest.key !== key) {
      const request = { key, promise: this.fetchSectionHtml('full') };
      // Drop a rejected entry so the next tap retries instead of replaying the failure.
      request.promise.catch(() => {
        if (this.fullRequest === request) this.fullRequest = null;
      });
      this.fullRequest = request;
    }
    return this.fullRequest.promise;
  },
  /**
   * Replace one capped filter group with its complete value list.
   * Resolves false and leaves the capped group untouched if anything goes wrong, so the
   * shopper keeps a working filter and the next tap can retry.
   */
  async expandFilter(details) {
    const param = details.dataset.filterParam;
    const content = details.querySelector('.accordion-details__content');
    if (!param || !content || details.dataset.facetExpanding === 'true') return false;

    details.dataset.facetExpanding = 'true';
    try {
      const html = await this.fullFacets();
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(`details[data-filter-param="${CSS.escape(param)}"] .accordion-details__content`);
      if (!source) return false;

      // The lazy section renders with context `full`, so its ids carry a `full-` prefix. Re-key them
      // onto this copy's context, or the labels stop addressing their inputs. Only rewrite when the
      // prefix can be derived from data-index — `full-` ids are at least self-consistent.
      const index = details.dataset.index || '';
      const prefix = index.endsWith(param) ? index.slice(0, index.length - param.length) : null;
      if (prefix !== null) {
        source.querySelectorAll('[id^="full-"]').forEach((element) => {
          element.id = prefix + element.id.slice('full-'.length);
        });
        source.querySelectorAll('label[for^="full-"]').forEach((element) => {
          element.setAttribute('for', prefix + element.getAttribute('for').slice('full-'.length));
        });
      }

      content.replaceWith(source);
      return true;
    } catch (error) {
      console.warn('[facets] expand failed', param, error);
      return false;
    } finally {
      delete details.dataset.facetExpanding;
    }
  },
  init() {
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('button[aria-controls="FacetDrawer"]');
        if (trigger) this.hydrate('drawer');

        const moreButton = event.target.closest('show-more-button[data-facet-more] button');
        if (!moreButton) return;
        const details = moreButton.closest('details[data-filter]');
        if (!details) return;

        // Stop show-more-button's own handler: it would flip the labels and reveal
        // `.show-more-item` nodes that a capped render never emitted.
        event.stopPropagation();
        event.preventDefault();
        moreButton.setAttribute('aria-busy', 'true');

        this.expandFilter(details).then((expanded) => {
          moreButton.removeAttribute('aria-busy');
          if (!expanded) return;
          // The grafted group is the uncapped markup: the extra values are present but hidden,
          // behind a fresh plain button. Click it so one tap reads as one expansion.
          const fresh = details.querySelector('show-more-button:not([data-facet-more]) button');
          if (fresh) fresh.click();
        });
      },
      true
    );

    // search-component filters the values already in the DOM, so on a capped group it would only
    // ever search the visible few. Pull the full list in as soon as the shopper puts the cursor in
    // the box, before there is anything to type.
    document.addEventListener(
      'focusin',
      (event) => {
        const input = event.target.closest('[data-search-component-input]');
        if (!input) return;
        const details = input.closest('details[data-filter]');
        if (!details || !details.querySelector('show-more-button[data-facet-more]')) return;

        this.expandFilter(details).then((expanded) => {
          if (!expanded) return;
          // The old input went with the replaced group — carry the caret and any text across.
          // Read `input.value` fresh here, not at focusin time: the old node is detached by
          // expandFilter's replaceWith but stays the shopper's focus target until then, so it
          // still picks up keystrokes typed while expandFilter was in flight.
          const value = input.value;
          const fresh = details.querySelector('[data-search-component-input]');
          if (!fresh) return;
          fresh.value = value;
          fresh.focus();
          if (value) fresh.dispatchEvent(new Event('input', { bubbles: true }));
        });
      },
      true
    );
  },
};

FoxTheme.FacetsLazy.init();
